/**
 * AnnotationReviewList Component
 * Photographer view of all client annotations for a gallery/job
 */

import React, { useState, useEffect, useRef } from 'react';
import { FiSquare, FiCircle, FiMessageSquare, FiCheck, FiClock, FiEye, FiX, FiChevronDown, FiChevronRight, FiChevronLeft, FiExternalLink, FiImage } from 'react-icons/fi';

const API_BASE = '/api/annotations';

// Shape colors matching the client-side canvas
const SHAPE_COLORS = {
  pending: '#f59e0b',    // amber
  reviewed: '#304849',   // blue
  addressed: '#10b981',  // emerald
  dismissed: '#435654',  // gray
};

// Helper to make authenticated fetch requests
const authFetch = (url, options = {}) => {
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...options.headers,
      'Content-Type': options.body ? 'application/json' : undefined
    }
  });
};

// Modal to view image with annotation shapes overlaid
function AnnotationImageViewer({ allImages, currentIndex, onNavigate, selectedAnnotationId, onSelect, onClose }) {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  const currentImage = allImages[currentIndex];
  const imageId = currentImage?.image_id;
  const annotations = currentImage?.annotations || [];

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < allImages.length - 1;

  useEffect(() => {
    if (imageId) {
      loadImage();
    }
  }, [imageId]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && canGoPrev) onNavigate(currentIndex - 1);
      if (e.key === 'ArrowRight' && canGoNext) onNavigate(currentIndex + 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, currentIndex, canGoPrev, canGoNext]);

  const loadImage = async () => {
    try {
      setLoading(true);
      // Use the images API to get image data with variant URLs
      const res = await fetch(`/api/images/${imageId}?variants=lg`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // The URL is in cdn_urls.lg
        setImageUrl(data.cdn_urls?.lg || data.cdn_urls?.md || null);
      }
    } catch (err) {
      console.error('Failed to load image:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleImageLoad = () => {
    if (imgRef.current) {
      setDimensions({
        width: imgRef.current.offsetWidth,
        height: imgRef.current.offsetHeight
      });
    }
  };

  // Render rectangle shape
  const renderRectangle = (annotation) => {
    const { x, y, width, height } = annotation.geometry;
    const color = SHAPE_COLORS[annotation.status] || SHAPE_COLORS.pending;
    const isSelected = annotation.id === selectedAnnotationId;

    return (
      <rect
        key={annotation.id}
        x={x * dimensions.width}
        y={y * dimensions.height}
        width={width * dimensions.width}
        height={height * dimensions.height}
        fill="transparent"
        stroke={color}
        strokeWidth={isSelected ? 4 : 2}
        strokeDasharray={annotation.status === 'dismissed' ? '5,5' : 'none'}
        style={{ cursor: 'pointer', opacity: annotation.status === 'dismissed' ? 0.5 : 1 }}
        onClick={() => onSelect(annotation.id)}
      />
    );
  };

  // Render circle shape
  const renderCircle = (annotation) => {
    const { cx, cy, radius } = annotation.geometry;
    const color = SHAPE_COLORS[annotation.status] || SHAPE_COLORS.pending;
    const isSelected = annotation.id === selectedAnnotationId;
    const minDimension = Math.min(dimensions.width, dimensions.height);

    return (
      <circle
        key={annotation.id}
        cx={cx * dimensions.width}
        cy={cy * dimensions.height}
        r={radius * minDimension}
        fill="transparent"
        stroke={color}
        strokeWidth={isSelected ? 4 : 2}
        strokeDasharray={annotation.status === 'dismissed' ? '5,5' : 'none'}
        style={{ cursor: 'pointer', opacity: annotation.status === 'dismissed' ? 0.5 : 1 }}
        onClick={() => onSelect(annotation.id)}
      />
    );
  };

  const selectedAnnotation = annotations.find(a => a.id === selectedAnnotationId);

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={onClose}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <FiImage size={20} className="shrink-0" />
          <span className="text-sm truncate">
            {currentIndex + 1} of {allImages.length} photos • {annotations.length} annotation{annotations.length !== 1 ? 's' : ''}
          </span>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg shrink-0 ml-4">
          <FiX size={24} />
        </button>
      </div>

      {/* Image with annotations and navigation */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden relative" onClick={(e) => e.stopPropagation()}>
        {/* Previous button */}
        {canGoPrev && (
          <button
            onClick={() => onNavigate(currentIndex - 1)}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full z-10 transition-colors"
            title="Previous photo (←)"
          >
            <FiChevronLeft size={28} />
          </button>
        )}

        {/* Next button */}
        {canGoNext && (
          <button
            onClick={() => onNavigate(currentIndex + 1)}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 text-white rounded-full z-10 transition-colors"
            title="Next photo (→)"
          >
            <FiChevronRight size={28} />
          </button>
        )}
        {loading ? (
          <div className="w-10 h-10 border-3 border-white border-t-transparent rounded-full animate-spin" />
        ) : imageUrl ? (
          <div ref={containerRef} className="relative max-w-full max-h-full">
            <img
              ref={imgRef}
              src={imageUrl}
              alt="Annotated photo"
              className="max-w-full max-h-[calc(100vh-200px)] object-contain"
              onLoad={handleImageLoad}
              draggable={false}
            />
            {dimensions.width > 0 && (
              <svg
                className="absolute inset-0 w-full h-full pointer-events-auto"
                style={{ width: dimensions.width, height: dimensions.height }}
              >
                {annotations.map(annotation => {
                  if (annotation.shape_type === 'rectangle') {
                    return renderRectangle(annotation);
                  }
                  if (annotation.shape_type === 'circle') {
                    return renderCircle(annotation);
                  }
                  return null;
                })}
              </svg>
            )}
          </div>
        ) : (
          <div className="text-white/60">Failed to load image</div>
        )}
      </div>

      {/* Selected annotation info */}
      {selectedAnnotation && (
        <div className="p-4 bg-gray-900 text-white border-t border-white/10">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-2">
              {selectedAnnotation.shape_type === 'circle' ? <FiCircle size={16} /> : <FiSquare size={16} />}
              <span className="text-sm font-medium" style={{ color: SHAPE_COLORS[selectedAnnotation.status] }}>
                {selectedAnnotation.status.charAt(0).toUpperCase() + selectedAnnotation.status.slice(1)}
              </span>
              {selectedAnnotation.client_name && (
                <span className="text-sm text-white/60">by {selectedAnnotation.client_name}</span>
              )}
            </div>
            {selectedAnnotation.comment && (
              <p className="text-white/90">{selectedAnnotation.comment}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', icon: FiClock },
  reviewed: { label: 'Reviewed', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200', icon: FiEye },
  addressed: { label: 'Addressed', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: FiCheck },
  dismissed: { label: 'Dismissed', color: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200', icon: FiX }
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${config.bg} ${config.color} ${config.border}`}>
      <Icon size={10} />
      {config.label}
    </span>
  );
}

function AnnotationCard({ annotation, onStatusChange, onRespond }) {
  const [expanded, setExpanded] = useState(false);
  const [response, setResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const ShapeIcon = annotation.shape_type === 'circle' ? FiCircle : FiSquare;

  const handleSubmitResponse = async (e) => {
    e.preventDefault();
    if (!response.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onRespond(annotation.id, response.trim(), newStatus || null);
      setResponse('');
      setNewStatus('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`border rounded-lg ${annotation.status === 'addressed' ? 'opacity-60' : ''}`}>
      <div
        className="p-3 flex items-start gap-3 cursor-pointer hover:bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="mt-0.5">
          {expanded ? <FiChevronDown size={16} /> : <FiChevronRight size={16} />}
        </div>
        <ShapeIcon className="w-4 h-4 mt-0.5 text-gray-400" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={annotation.status} />
            {annotation.client_name && (
              <span className="text-sm text-gray-600">{annotation.client_name}</span>
            )}
            <span className="text-xs text-gray-400">
              {new Date(annotation.created_at).toLocaleDateString()}
            </span>
          </div>
          {annotation.comment && (
            <p className="mt-1 text-sm text-gray-700 line-clamp-2">{annotation.comment}</p>
          )}
          {annotation.responses && annotation.responses.length > 0 && (
            <div className="mt-1 flex items-center gap-1 text-xs text-gray-500">
              <FiMessageSquare size={10} />
              {annotation.responses.length} response{annotation.responses.length !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 border-t bg-gray-50">
          {/* Full comment */}
          {annotation.comment && (
            <div className="pt-3">
              <p className="text-sm font-medium text-gray-500">Client Comment</p>
              <p className="mt-1 text-sm text-gray-700">{annotation.comment}</p>
            </div>
          )}

          {/* Existing responses */}
          {annotation.responses && annotation.responses.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-sm font-medium text-gray-500">Responses</p>
              {annotation.responses.map(resp => (
                <div key={resp.id} className="pl-3 border-l-2 border-blue-300">
                  <p className="text-sm text-gray-700">{resp.response}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {resp.user_name || 'You'} - {new Date(resp.created_at).toLocaleDateString()}
                    {resp.new_status && <span className="ml-2">Set to {resp.new_status}</span>}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Quick status buttons */}
          <div className="mt-3 flex items-center gap-2">
            <span className="text-xs text-gray-500">Quick status:</span>
            {['reviewed', 'addressed', 'dismissed'].map(status => (
              <button
                key={status}
                onClick={() => onStatusChange(annotation.id, status)}
                disabled={annotation.status === status}
                className={`text-xs px-2 py-1 rounded border transition-colors ${
                  annotation.status === status
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'hover:bg-gray-100'
                }`}
              >
                {STATUS_CONFIG[status].label}
              </button>
            ))}
          </div>

          {/* Response form */}
          <form onSubmit={handleSubmitResponse} className="mt-3">
            <textarea
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Add a response..."
              className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
              rows={2}
            />
            <div className="mt-2 flex items-center gap-2">
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="text-sm border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Keep status</option>
                <option value="reviewed">Mark reviewed</option>
                <option value="addressed">Mark addressed</option>
                <option value="dismissed">Dismiss</option>
              </select>
              <button
                type="submit"
                disabled={!response.trim() || isSubmitting}
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Response'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function ImageAnnotationGroup({ imageData, onStatusChange, onRespond, onViewImage }) {
  const [collapsed, setCollapsed] = useState(false);
  const pendingCount = imageData.annotations.filter(a => a.status === 'pending').length;

  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className="p-3 bg-gray-100 flex items-center gap-3 cursor-pointer hover:bg-gray-200"
        onClick={() => setCollapsed(!collapsed)}
      >
        {collapsed ? <FiChevronRight size={16} /> : <FiChevronDown size={16} />}
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-800">
              {imageData.image_title || imageData.filename}
            </span>
            <span className="text-sm text-gray-500">
              {imageData.annotations.length} annotation{imageData.annotations.length !== 1 ? 's' : ''}
            </span>
            {pendingCount > 0 && (
              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded">
                {pendingCount} pending
              </span>
            )}
          </div>
        </div>
        {onViewImage && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onViewImage(imageData.image_id, imageData.annotations);
            }}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
            title="View photo with annotations"
          >
            <FiImage size={14} />
            View Photo
          </button>
        )}
      </div>

      {!collapsed && (
        <div className="p-3 space-y-2">
          {imageData.annotations.map(annotation => (
            <AnnotationCard
              key={annotation.id}
              annotation={annotation}
              onStatusChange={onStatusChange}
              onRespond={onRespond}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function AnnotationReviewList({ galleryId, jobId, headers }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, pending, reviewed, addressed, dismissed

  // Image viewer state
  const [viewingImageIndex, setViewingImageIndex] = useState(null);
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);

  // Filter images based on status filter - computed early so handlers can use it
  const filteredImages = data?.images
    ? data.images
        .map(img => ({
          ...img,
          annotations: filter === 'all'
            ? img.annotations
            : img.annotations.filter(a => a.status === filter)
        }))
        .filter(img => img.annotations.length > 0)
    : [];

  useEffect(() => {
    loadAnnotations();
  }, [galleryId]);

  const handleViewImage = (imageId, annotations) => {
    // Find the index in filteredImages
    const index = filteredImages.findIndex(img => img.image_id === imageId);
    if (index !== -1) {
      setViewingImageIndex(index);
      setSelectedAnnotationId(annotations[0]?.id || null);
    }
  };

  const handleNavigateImage = (newIndex) => {
    setViewingImageIndex(newIndex);
    const newImage = filteredImages[newIndex];
    setSelectedAnnotationId(newImage?.annotations?.[0]?.id || null);
  };

  const handleCloseViewer = () => {
    setViewingImageIndex(null);
    setSelectedAnnotationId(null);
  };

  const loadAnnotations = async () => {
    try {
      setLoading(true);
      const endpoint = galleryId
        ? `${API_BASE}/gallery/${galleryId}`
        : `${API_BASE}/job/${jobId}/summary`;

      const res = await authFetch(endpoint);
      if (res.ok) {
        setData(await res.json());
      } else {
        setError('Failed to load annotations');
      }
    } catch (err) {
      console.error('Error loading annotations:', err);
      setError('Failed to load annotations');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (annotationId, newStatus) => {
    try {
      const res = await authFetch(`${API_BASE}/${annotationId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus })
      });

      if (res.ok) {
        // Update local state
        setData(prev => ({
          ...prev,
          images: prev.images.map(img => ({
            ...img,
            annotations: img.annotations.map(a =>
              a.id === annotationId ? { ...a, status: newStatus } : a
            )
          }))
        }));
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleRespond = async (annotationId, response, newStatus) => {
    try {
      const res = await authFetch(`${API_BASE}/${annotationId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ response, new_status: newStatus })
      });

      if (res.ok) {
        const newResponse = await res.json();
        // Update local state
        setData(prev => ({
          ...prev,
          images: prev.images.map(img => ({
            ...img,
            annotations: img.annotations.map(a =>
              a.id === annotationId
                ? {
                    ...a,
                    status: newStatus || a.status,
                    responses: [...(a.responses || []), newResponse]
                  }
                : a
            )
          }))
        }));
      }
    } catch (err) {
      console.error('Error submitting response:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {error}
      </div>
    );
  }

  if (!data || !data.images || data.images.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No annotations yet
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats summary */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="text-sm text-gray-600">
          <span className="font-medium">{data.stats?.total || 0}</span> total annotations
        </div>
        {data.stats?.pending > 0 && (
          <div className="text-sm text-amber-600">
            <span className="font-medium">{data.stats.pending}</span> pending
          </div>
        )}
        {data.stats?.reviewed > 0 && (
          <div className="text-sm text-blue-600">
            <span className="font-medium">{data.stats.reviewed}</span> reviewed
          </div>
        )}
        {data.stats?.addressed > 0 && (
          <div className="text-sm text-emerald-600">
            <span className="font-medium">{data.stats.addressed}</span> addressed
          </div>
        )}
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Filter:</span>
        {['all', 'pending', 'reviewed', 'addressed', 'dismissed'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`text-sm px-3 py-1 rounded-full border transition-colors ${
              filter === f
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {/* Annotation list by image */}
      <div className="space-y-3">
        {filteredImages.map(imageData => (
          <ImageAnnotationGroup
            key={imageData.image_id}
            imageData={imageData}
            onStatusChange={handleStatusChange}
            onRespond={handleRespond}
            onViewImage={handleViewImage}
          />
        ))}
      </div>

      {filteredImages.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No {filter !== 'all' ? filter : ''} annotations found
        </div>
      )}

      {/* Image viewer modal */}
      {viewingImageIndex !== null && filteredImages.length > 0 && (
        <AnnotationImageViewer
          allImages={filteredImages}
          currentIndex={viewingImageIndex}
          onNavigate={handleNavigateImage}
          selectedAnnotationId={selectedAnnotationId}
          onSelect={setSelectedAnnotationId}
          onClose={handleCloseViewer}
        />
      )}
    </div>
  );
}
